import React, { useEffect, useState } from 'react';
import { Eye, FileText, User, Send, X, Search, CheckCircle, ChevronDown, ChevronRight, ChevronUp, History, ArrowRight, CornerUpLeft, PlayCircle, Pen, PenTool, Calendar } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, UI_THEMES } from '../contexts/ThemeContext';
import DocumentSignModal from '../components/DocumentSignModal';
import { filterActiveSignatures } from '../utils/attachmentSigning';

const InvoicesReturns = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isModern = theme === UI_THEMES.MODERN;
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [returnInfo, setReturnInfo] = useState({});
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());
  const [invoiceHistory, setInvoiceHistory] = useState({});
  const [expandedHistory, setExpandedHistory] = useState(new Set()); // Stan do śledzenia rozwiniętych historii
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signingInvoiceId, setSigningInvoiceId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [invoiceDescriptions, setInvoiceDescriptions] = useState({});
  const [invoiceSignatures, setInvoiceSignatures] = useState({});
  const [editForm, setEditForm] = useState({
    description: '',
    signedByPosition: '',
    metricalChecked: false,
    preliminaryCheckDone: false,
    formalnoRachunkowoChecked: false,
    zatwierdzonoDo: '',
    kwota: '',
    costCenter: '',
    invoicePaymentType: 'TRANSFER', // CASH=gotówkowa, TRANSFER=przelew (wymagane przy canCheckMerit) - domyślnie Przelew
    isDotacja: false,
    srodkiPowiat: '',
    srodkiSpZoz: ''
  });
  const [saving, setSaving] = useState(false);
  const [isSignaturesCollapsed, setIsSignaturesCollapsed] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [assignBatchIds, setAssignBatchIds] = useState([]);
  const [batchSignQueue, setBatchSignQueue] = useState([]);

  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  };
  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  };
  const [dateFromFilter, setDateFromFilter] = useState(getFirstDayOfMonth());
  const [dateToFilter, setDateToFilter] = useState(getLastDayOfMonth());

  const toggleSelection = (id) => {
    setSelectedInvoiceIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const selectAllOnPage = (invList) => {
    if (!invList.length) return;
    const ids = invList.map(i => i.id);
    setSelectedInvoiceIds(prev => {
      const set = new Set(prev);
      const allSelected = ids.every(id => set.has(id));
      if (allSelected) ids.forEach(id => set.delete(id));
      else ids.forEach(id => set.add(id));
      return Array.from(set);
    });
  };
  const clearSelection = () => {
    setSelectedInvoiceIds([]);
    setBatchSignQueue([]);
  };
  const handleBatchSignClick = () => {
    if (selectedInvoiceIds.length === 0) return;
    const queue = [...selectedInvoiceIds];
    setBatchSignQueue(queue);
    setSigningInvoiceId(queue[0]);
    setShowSignModal(true);
  };
  const handleAssignBatchClick = async () => {
    if (selectedInvoiceIds.length === 0) return;
    setSelectedInvoice(null);
    setAssignBatchIds([...selectedInvoiceIds]);
    setSelectedUserId('');
    setUserSearchQuery('');
    await fetchUsers();
    setShowAssignModal(true);
  };

  useEffect(() => {
    fetchReturns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFromFilter, dateToFilter]);

  // Aktualizuj selectedInvoice gdy lista faktur się zmienia (np. po przyjęciu do realizacji)
  useEffect(() => {
    if (selectedInvoice && showDescriptionModal) {
      const updatedInvoice = invoices.find(inv => inv.id === selectedInvoice.id);
      if (updatedInvoice && updatedInvoice.status !== selectedInvoice.status) {
        setSelectedInvoice({ ...selectedInvoice, ...updatedInvoice });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, showDescriptionModal]);

  const fetchReturns = async () => {
    try {
      setLoading(true);
      const dateParams = {};
      if (dateFromFilter) dateParams.dateFrom = dateFromFilter;
      if (dateToFilter) dateParams.dateTo = dateToFilter;
      const [returnedToMeRes, returnedByMeRes] = await Promise.all([
        api.get('/invoices', { params: { returnedToMe: true, ...dateParams } }),
        api.get('/invoices', { params: { returnedByMe: true, ...dateParams } })
      ]);

      const returnedToMe = (returnedToMeRes.data.content || returnedToMeRes.data || []).map(inv => ({
        ...inv,
        returnType: 'TO_ME'
      }));
      const returnedByMe = (returnedByMeRes.data.content || returnedByMeRes.data || []).map(inv => ({
        ...inv,
        returnType: 'BY_ME'
      }));

      const combined = [...returnedToMe, ...returnedByMe];
      setInvoices(combined);

      if (combined.length > 0) {
        await fetchReturnInfo(combined);
      }
    } catch (error) {
      toast.error('Błąd podczas pobierania zwrotów');
      console.error('Error fetching returns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReturnInfo = async (list) => {
    const infoMap = {};
    await Promise.all(list.map(async (invoice) => {
      try {
        const response = await api.get(`/invoices/${invoice.id}/history`);
        const history = response.data || [];
        const returnedItem = history.find(item => item.actionType === 'RETURNED');
        if (returnedItem) {
          infoMap[invoice.id] = {
            reason: extractReturnReason(returnedItem.actionDescription),
            performedByUserName: returnedItem.performedByUserName,
            assignedToUserName: returnedItem.assignedToUserName,
            createdAt: returnedItem.createdAt
          };
        }
      } catch (error) {
        console.error('Error fetching return history:', error);
      }
    }));
    setReturnInfo(infoMap);
  };

  const extractReturnReason = (description) => {
    if (!description) return '';
    const lower = description.toLowerCase();
    const idx = lower.indexOf('powód:');
    if (idx === -1) return description;
    return description.substring(idx + 'powód:'.length).trim();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
  };

  const formatCurrency = (amount, currency = 'PLN') => {
    if (!amount) return '-';
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatHistoryDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleInvoiceExpansion = async (invoiceId) => {
    const newExpanded = new Set(expandedInvoices);
    if (newExpanded.has(invoiceId)) {
      newExpanded.delete(invoiceId);
    } else {
      newExpanded.add(invoiceId);
      if (!invoiceHistory[invoiceId]) {
        await fetchInvoiceHistory(invoiceId);
      }
    }
    setExpandedInvoices(newExpanded);
  };

  const fetchInvoiceHistory = async (invoiceId) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/history`);
      setInvoiceHistory(prev => ({
        ...prev,
        [invoiceId]: response.data
      }));
    } catch (error) {
      const isAborted = error?.code === 'ECONNABORTED' || error?.message === 'Request aborted' || error?.message === 'canceled';
      if (!isAborted) console.error('Error fetching invoice history:', error);
    }
  };

  const handleViewPdf = async (invoice) => {
    // W każdej zakładce: KSeF, PDF, skany – zawsze podgląd w nowym oknie z linkiem publicznym
    if (!invoice.id) return;
    if (!invoice.filePath && !invoice.ksefReference) {
      toast.error('Brak załącznika dla tej faktury');
      return;
    }
    const previewUrl = `/app/invoices/${invoice.id}/preview`;
    window.open(previewUrl, '_blank', 'width=1200,height=800');
  };

  const getReturnLabel = (type) => {
    if (type === 'TO_ME') return 'Zwrócona do mnie';
    return 'Zwrócona przeze mnie';
  };

  const handleForwardClick = (invoice) => {
    setSelectedInvoice(invoice);
    setSelectedUserId('');
    setUserSearchQuery('');
    setUsers([]);
    setShowAssignModal(true);
    fetchUsers();
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await api.get('/users/assignment/list');
      const data = response.data;
      const list = Array.isArray(data) ? data : (data?.content || data?.data || []);
      setUsers(list);
      if (list.length === 0 && !Array.isArray(data) && !data?.content) {
        console.warn('Users response format:', typeof data, data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Nie udało się pobrać listy użytkowników. Sprawdź połączenie.');
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAssignSubmit = async () => {
    if (!selectedUserId) {
      toast.error('Wybierz użytkownika');
      return;
    }

    const isBatch = assignBatchIds.length > 0;
    if (!selectedInvoice && !isBatch) {
      return;
    }

    if (isAssigning) {
      return;
    }

    setIsAssigning(true);
    try {
      const targetUserId = parseInt(selectedUserId);
      if (isBatch) {
        let ok = 0;
        let err = 0;
        for (const id of assignBatchIds) {
          try {
            await api.put(`/invoices/${id}`, { status: 'ASSIGNED', assignedUserId: targetUserId });
            ok++;
          } catch (e) {
            err++;
            console.error('Error assigning invoice', id, e);
          }
        }
        setShowAssignModal(false);
        setAssignBatchIds([]);
        setSelectedInvoiceIds([]);
        setSelectedInvoice(null);
        setSelectedUserId('');
        setUserSearchQuery('');
        await fetchReturns();
        if (err > 0) toast.error(`Przekazano ${ok} faktur. Błąd: ${err}.`);
        else toast.success(`Przekazano ${ok} ${ok === 1 ? 'fakturę' : ok < 5 ? 'faktury' : 'faktur'} do użytkownika`);
      } else {
        await api.put(`/invoices/${selectedInvoice.id}`, {
          status: 'ASSIGNED',
          assignedUserId: targetUserId
        });
        toast.success('Faktura przekazana do użytkownika');
        setShowAssignModal(false);
        setSelectedInvoice(null);
        setSelectedUserId('');
        setUserSearchQuery('');
        await fetchReturns();
      }
    } catch (error) {
      toast.error('Błąd podczas przekazywania faktury');
      console.error('Error assigning invoice:', error);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleAcceptToRealization = async (invoice) => {
    try {
      const response = await api.patch(`/invoices/${invoice.id}/accept`);
      const updatedInvoice = response.data;
      toast.success('Faktura została przyjęta do realizacji');
      
      // Odśwież listę zwrotów
      await fetchReturns();
      
      // Po odświeżeniu, upewnij się że faktura ma status IN_PROGRESS w liście
      setInvoices(prev => {
        const updated = prev.map(inv => 
          inv.id === invoice.id 
            ? { ...inv, status: 'IN_PROGRESS', assignedUserId: updatedInvoice.assignedUserId || inv.assignedUserId }
            : inv
        );
        
        // Jeśli modal jest otwarty, zaktualizuj selectedInvoice z najnowszych danych
        if (selectedInvoice && selectedInvoice.id === invoice.id) {
          const updatedFromList = updated.find(inv => inv.id === invoice.id);
          if (updatedFromList) {
            setSelectedInvoice({ ...updatedFromList, status: 'IN_PROGRESS' });
          }
        }
        
        return updated;
      });
      
      // Odśwież historię jeśli faktura jest rozwinięta
      if (expandedInvoices.has(invoice.id)) {
        await fetchInvoiceHistory(invoice.id);
      }
      
      // Odśwież opisy i podpisy
      await fetchInvoiceDescriptionsAndSignatures(invoice.id);
    } catch (error) {
      toast.error('Błąd podczas przyjmowania faktury do realizacji');
      console.error('Error accepting invoice:', error);
    }
  };

  const fetchInvoiceDescriptionsAndSignatures = async (invoiceId) => {
    try {
      const [descResponse, sigResponse] = await Promise.all([
        api.get(`/invoices/${invoiceId}/descriptions`),
        api.get(`/documents/invoices/${invoiceId}/signatures`)
      ]);
      setInvoiceDescriptions(prev => ({
        ...prev,
        [invoiceId]: descResponse.data || []
      }));
      setInvoiceSignatures(prev => ({
        ...prev,
        [invoiceId]: sigResponse.data || []
      }));
    } catch (error) {
      console.error('Error fetching descriptions and signatures:', error);
    }
  };

  const handleDescribeClick = async (invoice) => {
    setSelectedInvoice(invoice);
    setShowDescriptionModal(true);
    
    // Pobierz opisy i podpisy dla faktury
    await fetchInvoiceDescriptionsAndSignatures(invoice.id);
    
    // Załaduj istniejący opis użytkownika jeśli istnieje, ale pozwól na edycję i dodanie nowego opisu/podpisu
    try {
      const descResponse = await api.get(`/invoices/${invoice.id}/descriptions`);
      const userDesc = descResponse.data.find(d => d.userId === user?.id);
      if (userDesc) {
        // Załaduj istniejący opis, ale użytkownik może go zmienić i dodać nowy podpis
        setEditForm({
          description: userDesc.description || '',
          signedByPosition: userDesc.signedByPosition || user?.position || '',
          metricalChecked: userDesc.metricalChecked || false,
          preliminaryCheckDone: userDesc.preliminaryCheckDone || false,
          formalnoRachunkowoChecked: userDesc.formalnoRachunkowoChecked || false,
          zatwierdzonoDo: userDesc.zatwierdzonoDo || '',
          kwota: userDesc.kwota ? userDesc.kwota.toString() : '',
          costCenter: userDesc.costCenter || '',
          invoicePaymentType: userDesc.invoicePaymentType || 'TRANSFER',
          isDotacja: userDesc.isDotacja || false,
          srodkiPowiat: userDesc.srodkiPowiat != null ? userDesc.srodkiPowiat.toString() : '',
          srodkiSpZoz: userDesc.srodkiSpZoz != null ? userDesc.srodkiSpZoz.toString() : ''
        });
      } else {
        // Brak istniejącego opisu - zacznij od zera
        setEditForm({
          description: '',
          signedByPosition: user?.position || '',
          metricalChecked: false,
          preliminaryCheckDone: false,
          formalnoRachunkowoChecked: false,
          zatwierdzonoDo: '',
          kwota: '',
          costCenter: '',
          invoicePaymentType: 'TRANSFER',
          isDotacja: false,
          srodkiPowiat: '',
          srodkiSpZoz: ''
        });
      }
    } catch (error) {
      console.error('Error fetching descriptions:', error);
      setEditForm({
        description: '',
        signedByPosition: user?.position || '',
        metricalChecked: false,
        preliminaryCheckDone: false,
        formalnoRachunkowoChecked: false,
        zatwierdzonoDo: '',
        kwota: '',
        costCenter: '',
        invoicePaymentType: 'TRANSFER'
      });
    }
  };

  const handleSaveDescription = async (invoiceId) => {
    try {
      setSaving(true);
      const requestData = {
        description: editForm.description,
        signedByPosition: editForm.signedByPosition,
        signatureImage: null
      };
      
      if (user?.canCheckMerit) {
        requestData.metricalChecked = editForm.metricalChecked || false;
        if (editForm.invoicePaymentType) {
          requestData.invoicePaymentType = editForm.invoicePaymentType;
        }
      }
      
      if (user?.canDoPreliminaryCheck) {
        requestData.preliminaryCheckDone = editForm.preliminaryCheckDone || false;
      }
      
      if (user?.canCheckFormalAccounting) {
        requestData.formalnoRachunkowoChecked = editForm.formalnoRachunkowoChecked || false;
        if (editForm.zatwierdzonoDo) {
          requestData.zatwierdzonoDo = editForm.zatwierdzonoDo;
        }
        if (editForm.kwota) {
          requestData.kwota = editForm.kwota.replace(',', '.');
        }
      }
      
      // Dodaj pole ośrodka kosztów jeśli użytkownik ma uprawnienie - zawsze wysyłaj, nawet jeśli puste
      if (user?.canUseCostCenter) {
        requestData.costCenter = editForm.costCenter ? editForm.costCenter.trim() : null;
      }
      requestData.isDotacja = editForm.isDotacja || false;
      if (editForm.isDotacja) {
        requestData.srodkiPowiat = editForm.srodkiPowiat ? editForm.srodkiPowiat.toString().replace(',', '.') : null;
        requestData.srodkiSpZoz = editForm.srodkiSpZoz ? editForm.srodkiSpZoz.toString().replace(',', '.') : null;
      }
      
      await api.patch(`/invoices/${invoiceId}/describe`, requestData);
      toast.success('Zapisano opis');
      
      await fetchInvoiceDescriptionsAndSignatures(invoiceId);
      await fetchReturns();
      
      if (expandedInvoices.has(invoiceId)) {
        await fetchInvoiceHistory(invoiceId);
      }
    } catch (error) {
      toast.error('Błąd podczas zapisywania opisu');
      console.error('Error saving description:', error);
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const query = userSearchQuery.toLowerCase();
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = (user.email || '').toLowerCase();
    return fullName.includes(query) || email.includes(query);
  });

  return (
    <div className={`card shadow-md border border-gray-200 rounded-xl overflow-hidden ${isModern ? 'crm-modern-invoices' : ''}`}>
      <div className="card-header bg-white px-4 py-4 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Zwroty faktur</h2>
            <p className="text-sm text-gray-500">
              {invoices.length === 1 ? '1 zwrot' : `${invoices.length} zwrotów`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs sm:text-sm text-gray-700 whitespace-nowrap hidden sm:inline">Data dodania:</label>
            <div className="relative">
              <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                className="pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <span className="text-gray-500">–</span>
            <div className="relative">
              <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="date"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                className="pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="card-body p-0">
        {loading ? (
          <div className="py-12">
            <LoadingSpinner />
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-16 px-6">
            <FileText className="mx-auto h-16 w-16 text-gray-300" />
            <h3 className="mt-4 text-lg font-semibold text-gray-900">Brak zwrotów</h3>
            <p className="mt-2 text-sm text-gray-500">
              {dateFromFilter || dateToFilter
                ? `Brak zwrotów w zakresie dat dodania (${dateFromFilter || '…'} – ${dateToFilter || '…'}). Poszerz zakres, aby zobaczyć starsze wpisy.`
                : 'Nie ma zwróconych faktur do wyświetlenia.'}
            </p>
          </div>
        ) : (
          <div className="w-full overflow-x-hidden">
            {selectedInvoiceIds.length > 0 && (
              <div className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4 border-b ${isModern ? 'bg-slate-50 border-slate-200' : 'bg-teal-600 text-white border-teal-700 shadow-md'}`}>
                <span className={`text-sm sm:text-base font-semibold ${isModern ? 'text-slate-700' : ''}`}>
                  {isModern && (
                    <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 mr-2 rounded-full bg-teal-100 text-teal-700 text-xs font-bold align-middle">
                      {selectedInvoiceIds.length}
                    </span>
                  )}
                  {isModern
                    ? `${selectedInvoiceIds.length === 1 ? 'faktura zaznaczona' : selectedInvoiceIds.length < 5 ? 'faktury zaznaczone' : 'faktur zaznaczonych'}`
                    : `Zaznaczono ${selectedInvoiceIds.length} ${selectedInvoiceIds.length === 1 ? 'fakturę' : selectedInvoiceIds.length < 5 ? 'faktury' : 'faktur'}`}
                </span>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={clearSelection}
                    className={isModern
                      ? 'px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors'
                      : 'px-3 py-1.5 text-sm font-medium text-teal-800 bg-white/90 rounded-lg hover:bg-white transition-colors'}
                  >
                    Wyczyść zaznaczenie
                  </button>
                  <button
                    type="button"
                    onClick={handleBatchSignClick}
                    className={isModern
                      ? 'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors'
                      : 'inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-teal-900 bg-white rounded-lg hover:bg-teal-50 transition-colors shadow-sm'}
                  >
                    <PenTool className="h-4 w-4" />
                    Podpisz wszystkie
                  </button>
                  <button
                    type="button"
                    onClick={handleAssignBatchClick}
                    className={isModern
                      ? 'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors'
                      : 'inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-teal-900 bg-white rounded-lg hover:bg-teal-50 transition-colors shadow-sm'}
                  >
                    <Send className="h-4 w-4" />
                    Wyślij do
                  </button>
                </div>
              </div>
            )}
            <table className="w-full divide-y divide-gray-200 table-fixed">
              <colgroup>
                <col style={{ width: 36 }} />
              </colgroup>
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-0.5 py-2 text-center" style={{ width: 36 }}>
                    {isModern ? (
                      <input
                        type="checkbox"
                        onChange={() => selectAllOnPage(invoices)}
                        checked={invoices.length > 0 && invoices.every(inv => selectedInvoiceIds.includes(inv.id))}
                        disabled={invoices.length === 0}
                        className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed align-middle"
                        title={invoices.every(inv => selectedInvoiceIds.includes(inv.id)) ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => selectAllOnPage(invoices)}
                        className="text-[10px] text-teal-600 hover:text-teal-700 font-medium leading-tight"
                        title={invoices.every(inv => selectedInvoiceIds.includes(inv.id)) ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
                      >
                        {invoices.every(inv => selectedInvoiceIds.includes(inv.id)) ? 'Odznacz' : 'Zaznacz'}
                      </button>
                    )}
                  </th>
                  <th className="px-1.5 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider w-[15%]">
                    Numer faktury
                  </th>
                  <th className="px-1.5 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell w-[18%]">
                    Dostawca
                  </th>
                  <th className="px-1.5 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider w-[9%]">
                    Data wystawienia
                  </th>
                  <th className="px-1.5 sm:px-2 py-2 text-right text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider w-[9%]">
                    Brutto
                  </th>
                  <th className="px-1.5 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider w-[12%]">
                    Typ zwrotu
                  </th>
                  <th className="px-1.5 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider w-[18%]">
                    Powód zwrotu
                  </th>
                  <th className="px-1.5 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider hidden lg:table-cell w-[9%]">
                    Użytkownik
                  </th>
                  <th className="px-1 sm:px-1.5 py-2 text-center text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wider w-[5%]">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {invoices.map((invoice) => {
                  const info = returnInfo[invoice.id] || {};
                  const isToMe = invoice.returnType === 'TO_ME';
                  const userLabel = isToMe ? 'Zwrócono przez' : 'Zwrócono do';
                  const userName = isToMe ? info.performedByUserName : info.assignedToUserName;
                  const showReason = info.reason || '-';
                  const isRowSelected = selectedInvoiceIds.includes(invoice.id);
                  return (
                    <React.Fragment key={`${invoice.id}_${invoice.returnType}`}>
                      <tr
                        className={isModern
                          ? 'hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100'
                          : 'hover:bg-blue-50/30 transition-colors duration-150 border-b border-gray-100'}
                        style={isRowSelected ? { backgroundColor: '#f0fdfa', background: '#f0fdfa' } : {}}
                      >
                        <td className="px-0.5 py-1.5 text-center align-middle" style={{ width: 36 }}>
                          {isModern ? (
                            <input
                              type="checkbox"
                              checked={isRowSelected}
                              onChange={() => toggleSelection(invoice.id)}
                              className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer align-middle"
                              aria-label={isRowSelected ? 'Odznacz fakturę' : 'Zaznacz fakturę'}
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => toggleSelection(invoice.id)}
                              className="inline-flex items-center justify-center w-3 h-3 rounded-full border transition-all focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-0.5"
                              style={isRowSelected ? { backgroundColor: '#14b8a6', borderColor: '#14b8a6', borderWidth: '1px' } : { backgroundColor: '#fff', borderColor: '#cbd5e1', borderWidth: '1px' }}
                              aria-label={isRowSelected ? 'Odznacz fakturę' : 'Zaznacz fakturę'}
                            />
                          )}
                        </td>
                        <td className="px-1.5 sm:px-2 py-2">
                          <div className="text-xs sm:text-sm font-medium text-gray-900 flex items-center gap-1 min-w-0">
                            <button
                              onClick={() => toggleInvoiceExpansion(invoice.id)}
                              className="text-gray-400 hover:text-gray-600 transition-colors p-0.5 rounded hover:bg-gray-100 flex-shrink-0"
                              aria-label={expandedInvoices.has(invoice.id) ? 'Zwiń' : 'Rozwiń'}
                            >
                              {expandedInvoices.has(invoice.id) ? (
                                <ChevronDown className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                              ) : (
                                <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                              )}
                            </button>
                            <FileText className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 flex-shrink-0" />
                            <div className="flex flex-col min-w-0">
                              <span className="font-semibold break-words min-w-0 text-xs sm:text-sm">{invoice.number}</span>
                              {invoice.internalNumber && (
                                <span className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
                                  Nr wewn.: {invoice.internalNumber}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      <td className="px-1.5 sm:px-2 py-2 hidden md:table-cell">
                        <div className="text-xs sm:text-sm text-gray-900 break-words line-clamp-2" title={invoice.supplierName}>
                          {invoice.supplierName || <span className="text-gray-400">-</span>}
                        </div>
                      </td>
                      <td className="px-1.5 sm:px-2 py-2">
                        <div className="text-xs sm:text-sm text-gray-700">
                          {formatDate(invoice.issueDate)}
                        </div>
                      </td>
                      <td className="px-1.5 sm:px-2 py-2 text-right">
                        <div className="text-xs sm:text-sm font-semibold text-gray-900">
                          {formatCurrency(invoice.amountGross, invoice.currency)}
                        </div>
                      </td>
                      <td className="px-1.5 sm:px-2 py-2">
                        <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 inline-flex text-[10px] sm:text-xs leading-4 sm:leading-5 font-semibold rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                          {getReturnLabel(invoice.returnType)}
                        </span>
                      </td>
                      <td className="px-1.5 sm:px-2 py-2">
                        <div className="text-[10px] sm:text-xs text-gray-700 whitespace-pre-wrap break-words">
                          {showReason}
                        </div>
                        {info.createdAt && (
                          <div className="text-[9px] sm:text-[10px] text-gray-500 mt-1">
                            {formatDate(info.createdAt)}
                          </div>
                        )}
                      </td>
                      <td className="px-1.5 sm:px-2 py-2 hidden lg:table-cell">
                        {userName ? (
                          <div className="text-[10px] sm:text-xs text-gray-700">
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3 text-gray-400" />
                              <span className="font-semibold">{userLabel}:</span>
                            </div>
                            <div className="mt-0.5 truncate">{userName}</div>
                          </div>
                        ) : (
                          <span className="text-xs sm:text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-1 sm:px-1.5 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {(invoice.filePath || invoice.ksefReference) && (
                            <button
                              onClick={() => handleViewPdf(invoice)}
                              className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors duration-150"
                              title={invoice.ksefReference ? "Podgląd faktury z KSeF" : "Podgląd faktury"}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {/* Zawsze pokazuj przyciski dla faktur zwróconych do mnie */}
                          {(() => {
                            if (isToMe) {
                              // Sprawdź czy faktura została przekazana dalej - jeśli tak, ikonka powinna być nieaktywna
                              // Faktura może być przyjęta tylko jeśli:
                              // 1. Jest przypisana do bieżącego użytkownika (assignedUserId === user?.id)
                              // 2. Ma status ASSIGNED lub RETURNED
                              // 3. NIE została przekazana dalej po zwrocie (sprawdź historię)
                              const history = invoiceHistory[invoice.id] || [];
                              const hasBeenForwardedAfterReturn = history.some(item => {
                                // Sprawdź czy po zwrocie (RETURNED) była akcja ASSIGNED do innego użytkownika
                                const returnIndex = history.findIndex(h => h.actionType === 'RETURNED');
                                if (returnIndex === -1) return false;
                                
                                // Historia jest DESC (najnowsze pierwsze) - slice(0, returnIndex) = zdarzenia po zwrocie
                                const eventsAfterReturn = history.slice(0, returnIndex);
                                const assignedAfterReturn = eventsAfterReturn.find(h => 
                                  h.actionType === 'ASSIGNED' && 
                                  h.assignedToUserId != null && 
                                  String(h.assignedToUserId) !== String(user?.id)
                                );
                                return assignedAfterReturn !== undefined;
                              });
                              
                              // Używamy == zamiast === aby obsłużyć różnice typów (number vs string z API)
                              const isAssignedToMe = invoice.assignedUserId != null && user?.id != null && 
                                String(invoice.assignedUserId) === String(user.id);
                              const canAccept = isAssignedToMe && 
                                                (invoice.status === 'ASSIGNED' || invoice.status === 'RETURNED') &&
                                                !hasBeenForwardedAfterReturn;
                              
                              const canDescribe = isAssignedToMe && 
                                                  invoice.status === 'IN_PROGRESS';
                              
                              return (
                                <>
                                  {canAccept && (
                                    <button
                                      onClick={() => {
                                        if (!canAccept) return;
                                        handleAcceptToRealization(invoice);
                                      }}
                                      disabled={!canAccept}
                                      className={`p-0.5 sm:p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors duration-150 flex-shrink-0 ${
                                        !canAccept ? 'opacity-50 cursor-not-allowed' : ''
                                      }`}
                                      title={canAccept ? "Przyjmij do realizacji" : "Faktura została przekazana dalej"}
                                    >
                                      <PlayCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                                    </button>
                                  )}
                                  {canDescribe && (
                                    <button
                                      onClick={() => handleDescribeClick(invoice)}
                                      className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors duration-150 flex-shrink-0"
                                      title="Opisz i podpisz fakturę"
                                    >
                                      <Pen className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleForwardClick(invoice)}
                                    className="p-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded transition-colors duration-150 flex-shrink-0"
                                    title="Przekaż dalej"
                                  >
                                    <Send className="h-3.5 w-3.5" />
                                  </button>
                                </>
                              );
                            } else {
                              return null;
                            }
                          })()}
                        </div>
                      </td>
                    </tr>
                    {expandedInvoices.has(invoice.id) && (
                      <tr>
                        <td colSpan={8} className="px-3 sm:px-4 md:px-6 py-4 sm:py-5 md:py-6 bg-gray-50/50">
                          <div className="ml-2 sm:ml-4 md:ml-8 space-y-4 sm:space-y-5">
                            {/* Historia faktury - wyświetl jako małe kwadraciki obok siebie ze strzałkami */}
                            {invoiceHistory[invoice.id] && invoiceHistory[invoice.id].length > 0 && (
                              <div className={isModern ? 'bg-white p-3 sm:p-4 rounded-xl border border-gray-100 shadow-sm' : 'bg-white p-3 sm:p-4 rounded-lg border border-gray-200 shadow-sm'}>
                                <button
                                  onClick={() => {
                                    const newExpanded = new Set(expandedHistory);
                                    if (newExpanded.has(invoice.id)) {
                                      newExpanded.delete(invoice.id);
                                    } else {
                                      newExpanded.add(invoice.id);
                                    }
                                    setExpandedHistory(newExpanded);
                                  }}
                                  className="w-full text-left"
                                >
                                  <h4 className={isModern ? 'text-sm font-semibold text-gray-800 mb-1 flex items-center justify-between gap-2' : 'text-sm font-normal text-gray-900 mb-3 flex items-center justify-between gap-2'}>
                                    <span className="flex items-center gap-2">
                                      {isModern ? (
                                        <span className="h-6 w-6 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
                                          <History className="h-3.5 w-3.5" />
                                        </span>
                                      ) : (
                                        <History className="h-4 w-4 text-gray-600" />
                                      )}
                                      Historia faktury
                                      <span className="text-xs text-gray-500 font-normal">
                                        {(() => {
                                          // Filtruj zmiany statusu - nie pokazuj ich w historii (są zbędne)
                                          // ALE pokazuj wpisy o zakończeniu faktury
                                          const filteredHistory = invoiceHistory[invoice.id].filter(item => 
                                            item.actionType !== 'STATUS_CHANGED' || 
                                            (item.actionDescription && item.actionDescription.toLowerCase().includes('zakończono'))
                                          );
                                          const count = filteredHistory.length;
                                          return `(${count} ${count === 1 ? 'wydarzenie' : 'wydarzeń'})`;
                                        })()}
                                      </span>
                                    </span>
                                    {expandedHistory.has(invoice.id) ? (
                                      <ChevronUp className="h-4 w-4 text-gray-600" />
                                    ) : (
                                      <ChevronDown className="h-4 w-4 text-gray-600" />
                                    )}
                                  </h4>
                                </button>
                                {expandedHistory.has(invoice.id) && (
                                <div className={isModern ? 'flex flex-wrap items-start gap-2 mt-3' : 'flex flex-wrap items-center gap-2 sm:gap-3'}>
                                  {(() => {
                                    // Odwróć kolejność - najstarsze pierwsze (od lewej do prawej)
                                    // Filtruj zmiany statusu - nie pokazuj ich w historii (są zbędne)
                                    // ALE pokazuj wpisy o zakończeniu faktury
                                    const filteredHistory = invoiceHistory[invoice.id].filter(item => 
                                      item.actionType !== 'STATUS_CHANGED' || 
                                      (item.actionDescription && item.actionDescription.toLowerCase().includes('zakończono'))
                                    );
                                    const reversedHistory = [...filteredHistory].reverse();
                                    return reversedHistory.map((historyItem, idx) => {
                                      const isLast = idx === reversedHistory.length - 1;
                                      const actionType = historyItem.actionType;
                                      
                                      // Określ ikonę i kolory na podstawie typu akcji
                                      let icon, bgColor, borderColor, textColor, iconColor;
                                      
                                      if (actionType === 'CREATED') {
                                        icon = FileText;
                                        bgColor = 'bg-blue-50';
                                        borderColor = 'border-blue-200';
                                        textColor = 'text-blue-900';
                                        iconColor = 'text-blue-600';
                                      } else if (actionType === 'ASSIGNED') {
                                        icon = Send;
                                        bgColor = 'bg-purple-50';
                                        borderColor = 'border-purple-200';
                                        textColor = 'text-purple-900';
                                        iconColor = 'text-purple-600';
                                      } else if (actionType === 'RETURNED') {
                                        icon = CornerUpLeft;
                                        bgColor = 'bg-orange-50';
                                        borderColor = 'border-orange-200';
                                        textColor = 'text-orange-900';
                                        iconColor = 'text-orange-600';
                                      } else if (actionType === 'UPDATED' && historyItem.actionDescription && (historyItem.actionDescription.includes('załącznik') || historyItem.actionDescription.toLowerCase().includes('zalacznik'))) {
                                        icon = FileText;
                                        bgColor = 'bg-blue-50';
                                        borderColor = 'border-blue-200';
                                        textColor = 'text-blue-900';
                                        iconColor = 'text-blue-600';
                                      } else if (actionType === 'UPDATED' && historyItem.actionDescription && historyItem.actionDescription.includes('opisał i podpisał')) {
                                        icon = FileText;
                                        bgColor = 'bg-green-50';
                                        borderColor = 'border-green-200';
                                        textColor = 'text-green-900';
                                        iconColor = 'text-green-600';
                                      } else if (actionType === 'STATUS_CHANGED') {
                                        // Sprawdź czy to zakończenie faktury
                                        if (historyItem.actionDescription && historyItem.actionDescription.toLowerCase().includes('zakończono')) {
                                          icon = CheckCircle;
                                          bgColor = 'bg-green-50';
                                          borderColor = 'border-green-200';
                                          textColor = 'text-green-900';
                                          iconColor = 'text-green-600';
                                        } else {
                                          icon = User;
                                          bgColor = 'bg-amber-50';
                                          borderColor = 'border-amber-200';
                                          textColor = 'text-amber-900';
                                          iconColor = 'text-amber-600';
                                        }
                                      } else {
                                        icon = History;
                                        bgColor = 'bg-gray-50';
                                        borderColor = 'border-gray-200';
                                        textColor = 'text-gray-900';
                                        iconColor = 'text-gray-600';
                                      }
                                      
                                      const IconComponent = icon;
                                      const modernAccent = borderColor.replace('border-', 'border-l-').replace('-200', '-400');
                                      
                                      return (
                                        <React.Fragment key={historyItem.id}>
                                          <div className={isModern
                                            ? `bg-white border border-gray-100 border-l-4 ${modernAccent} rounded-lg p-2.5 shadow-sm hover:shadow-md transition-shadow flex-shrink-0 w-fit max-w-[240px]`
                                            : `${bgColor} ${borderColor} border rounded-lg p-3 flex-shrink-0 min-w-[200px] sm:min-w-[220px] max-w-[280px]`}>
                                            <div className="flex flex-col">
                                              <div className={`flex items-center ${iconColor} mb-2`}>
                                                <IconComponent className="h-4 w-4 mr-2" />
                                                <div className={`text-xs font-normal ${textColor}`}>
                                                  {actionType === 'CREATED' ? 'Utworzenie' :
                                                   actionType === 'ASSIGNED' ? 'Przekazanie' :
                                                   actionType === 'RETURNED' ? 'Zwrot' :
                                                   (actionType === 'UPDATED' && historyItem.actionDescription && historyItem.actionDescription.includes('podpisał załącznik')) ? 'Podpisanie załącznika' :
                                                   actionType === 'UPDATED' ? 'Opisanie i podpisanie' :
                                                   actionType === 'UNASSIGNED' ? 'Cofnięcie' : 'Akcja'}
                                                </div>
                                              </div>
                                              <div className={`text-xs ${textColor} mb-2 leading-relaxed`}>
                                                {historyItem.actionDescription}
                                              </div>
                                              {historyItem.performedByUserName && (
                                                <div className={`text-xs ${textColor} opacity-75 mb-1 flex items-center`}>
                                                  <User className="h-3 w-3 mr-1" />
                                                  {historyItem.performedByUserName}
                                                </div>
                                              )}
                                              {historyItem.assignedToUserName && (
                                                <div className={`text-xs ${textColor} opacity-75 mb-1 flex items-center`}>
                                                  <ArrowRight className="h-3 w-3 mr-1" />
                                                  {actionType === 'RETURNED' ? 'Zwrócono do:' : 'Przekazano do:'} {historyItem.assignedToUserName}
                                                </div>
                                              )}
                                              <div className={`text-xs ${textColor} opacity-60 mt-auto pt-1`}>
                                                {formatHistoryDate(historyItem.createdAt)}
                                              </div>
                                            </div>
                                          </div>
                                          {!isLast && (
                                            <div className="flex items-center justify-center flex-shrink-0">
                                              <ArrowRight className="h-4 w-4 text-gray-400" />
                                            </div>
                                          )}
                                        </React.Fragment>
                                      );
                                    });
                                  })()}
                                </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal przekazania faktury */}
      {showAssignModal && (selectedInvoice || assignBatchIds.length > 0) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {assignBatchIds.length > 0 ? 'Przekaż faktury' : 'Przekaż fakturę'}
                </h3>
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedInvoice(null);
                    setAssignBatchIds([]);
                    setSelectedUserId('');
                    setUserSearchQuery('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="mb-4">
                {assignBatchIds.length > 0 ? (
                  <p className="text-sm text-gray-600 mb-2">
                    Przekaż <strong className="font-semibold text-gray-900">{assignBatchIds.length}</strong>{' '}
                    {assignBatchIds.length === 1 ? 'fakturę' : assignBatchIds.length < 5 ? 'faktury' : 'faktur'} do wybranego użytkownika.
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 mb-2">
                      Faktura: <span className="font-semibold">{selectedInvoice.number}</span>
                    </p>
                    <p className="text-sm text-gray-600">
                      Dostawca: <span className="font-semibold">{selectedInvoice.supplierName || '-'}</span>
                    </p>
                  </>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Wybierz użytkownika
                </label>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Szukaj użytkownika..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                  {usersLoading ? (
                    <div className="p-4 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-500 border-t-transparent" />
                      Ładowanie listy użytkowników...
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      {users.length === 0 ? 'Brak użytkowników (nie udało się pobrać listy)' : 'Brak użytkowników pasujących do wyszukiwania'}
                    </div>
                  ) : (
                    filteredUsers.map((user) => (
                      <button
                        key={user.id}
                        onClick={() => setSelectedUserId(user.id.toString())}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                          selectedUserId === user.id.toString() ? 'bg-purple-50 border-l-4 border-purple-500' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <User className="h-4 w-4 text-purple-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {user.email}
                            </div>
                          </div>
                          {selectedUserId === user.id.toString() && (
                            <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleAssignSubmit}
                  disabled={!selectedUserId || isAssigning}
                  className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isAssigning ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Przekazywanie...</span>
                    </>
                  ) : (
                    <span>Przekaż</span>
                  )}
                </button>
                <button
                  onClick={() => {
                    if (isAssigning) return;
                    setShowAssignModal(false);
                    setSelectedInvoice(null);
                    setAssignBatchIds([]);
                    setSelectedUserId('');
                    setUserSearchQuery('');
                  }}
                  disabled={isAssigning}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anuluj
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal opisu faktury */}
      {showDescriptionModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg shadow-xl w-[98vw] max-w-[1200px] max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Opisz fakturę</h3>
              <button
                onClick={() => {
                  setShowDescriptionModal(false);
                  setSelectedInvoice(null);
                  setEditForm({
                    description: '',
                    signedByPosition: '',
                    metricalChecked: false,
                    preliminaryCheckDone: false,
                    formalnoRachunkowoChecked: false,
                    zatwierdzonoDo: '',
                    kwota: '',
                    costCenter: '',
                    invoicePaymentType: 'TRANSFER',
                    isDotacja: false,
                    srodkiPowiat: '',
                    srodkiSpZoz: ''
                  });
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Podpis elektroniczny dokumentu – na górze */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Podpis elektroniczny dokumentu
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsSignaturesCollapsed(!isSignaturesCollapsed)}
                    className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                    title={isSignaturesCollapsed ? 'Rozwiń' : 'Zwiń'}
                  >
                    {isSignaturesCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                  </button>
                </div>
                {!isSignaturesCollapsed && (filterActiveSignatures(invoiceSignatures[selectedInvoice?.id]).length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {filterActiveSignatures(invoiceSignatures[selectedInvoice.id]).map((sig) => (
                      <div key={sig.id} className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="text-sm font-semibold text-gray-900">
                          {sig.user ? `${sig.user.firstName} ${sig.user.lastName}` : 'Nieznany użytkownik'}
                        </div>
                        <div className="text-xs text-gray-600">
                          {new Date(sig.signedAt).toLocaleString('pl-PL')}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                    Brak podpisów do wyświetlenia
                  </div>
                ))}
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Opis faktury <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="np. Zgodnie z umową nr 123/2024..."
                />
              </div>
              
              {user?.canCheckMerit && (
                <div className="space-y-3">
                  <div className="flex items-start bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 shadow-md">
                    <input
                      type="checkbox"
                      id="metricalChecked"
                      checked={editForm.metricalChecked || false}
                      onChange={(e) => setEditForm({ ...editForm, metricalChecked: e.target.checked })}
                      className="mt-0.5 h-6 w-6 text-blue-600 focus:ring-blue-500 border-gray-400 rounded cursor-pointer"
                    />
                    <label htmlFor="metricalChecked" className="ml-4 text-base font-bold text-gray-900 cursor-pointer">
                      SPRAWDZONO POD WZGLĘDEM MERYTORYCZNYM
                    </label>
                  </div>
                  <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 shadow-md">
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Typ płatności faktury <span className="text-red-600">*</span>
                    </label>
                    <p className="text-xs text-amber-800 mb-3">Wymagane przed podpisaniem dokumentu</p>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="invoicePaymentType"
                          value="CASH"
                          checked={editForm.invoicePaymentType === 'CASH'}
                          onChange={(e) => setEditForm({ ...editForm, invoicePaymentType: e.target.value })}
                          className="h-4 w-4 text-amber-600 focus:ring-amber-500"
                        />
                        <span className="text-sm font-medium">Faktura gotówkowa</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="invoicePaymentType"
                          value="TRANSFER"
                          checked={editForm.invoicePaymentType === 'TRANSFER'}
                          onChange={(e) => setEditForm({ ...editForm, invoicePaymentType: e.target.value })}
                          className="h-4 w-4 text-amber-600 focus:ring-amber-500"
                        />
                        <span className="text-sm font-medium">Przelew</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
              
              {user?.canDoPreliminaryCheck && (
                <div className="flex items-start bg-blue-50 border-2 border-blue-400 rounded-lg p-4 shadow-md">
                  <input
                    type="checkbox"
                    id="preliminaryCheckDone"
                    checked={editForm.preliminaryCheckDone || false}
                    onChange={(e) => setEditForm({ ...editForm, preliminaryCheckDone: e.target.checked })}
                    className="mt-0.5 h-6 w-6 text-blue-600 focus:ring-blue-500 border-gray-400 rounded cursor-pointer"
                  />
                  <label htmlFor="preliminaryCheckDone" className="ml-4 text-base font-bold text-gray-900 cursor-pointer">
                    DOKONANO WSTĘPNEJ KONTROLI
                  </label>
                </div>
              )}
              
              {user?.canCheckFormalAccounting && (
                <div className="bg-green-50 border-2 border-green-400 rounded-lg p-4 shadow-md space-y-4">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="formalnoRachunkowoChecked"
                      checked={editForm.formalnoRachunkowoChecked || false}
                      onChange={(e) => setEditForm({ ...editForm, formalnoRachunkowoChecked: e.target.checked })}
                      className="mt-0.5 h-6 w-6 text-green-600 focus:ring-green-500 border-gray-400 rounded cursor-pointer"
                    />
                    <label htmlFor="formalnoRachunkowoChecked" className="ml-4 text-base font-bold text-gray-900 cursor-pointer">
                      SPRAWDZONO POD WZGLĘDEM FORMALNO-RACHUNKOWYM
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ZATWIERDZONO DO: <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={editForm.zatwierdzonoDo}
                      onChange={(e) => setEditForm({ ...editForm, zatwierdzonoDo: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                    >
                      <option value="">Wybierz...</option>
                      <option value="WYPŁATY">WYPŁATY</option>
                      <option value="ZWROTU">ZWROTU</option>
                      <option value="REALIZACJI">REALIZACJI</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      KWOTĘ: <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editForm.kwota}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9,.]/g, '').replace(',', '.');
                        setEditForm({ ...editForm, kwota: value });
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                      placeholder="np. 1234.56"
                    />
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Stanowisko osoby opisującej <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editForm.signedByPosition}
                  onChange={(e) => setEditForm({ ...editForm, signedByPosition: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="np. Kierownik działu"
                />
              </div>
              {/* Pole Ośrodek kosztów - tylko dla użytkowników z uprawnieniem */}
              {user?.canUseCostCenter && (
                <div className="flex items-start bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 shadow-md">
                  <div className="flex-1">
                    <label htmlFor="costCenter" className="block text-base font-bold text-gray-900 mb-2 cursor-pointer select-none">
                      OŚRODEK KOSZTÓW
                    </label>
                    <input
                      type="text"
                      id="costCenter"
                      value={editForm.costCenter || ''}
                      onChange={(e) => setEditForm({ ...editForm, costCenter: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-yellow-500 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-600 text-sm font-semibold bg-white"
                      placeholder="np. 1234"
                    />
                  </div>
                </div>
              )}

              {/* DOTACJA (dotyczy księgowości) - widoczne dla wszystkich */}
              <div className="flex items-start bg-purple-50 border-2 border-purple-400 rounded-lg p-4 shadow-md">
                <input
                  type="checkbox"
                  id="isDotacja"
                  checked={editForm.isDotacja || false}
                  onChange={(e) => setEditForm({ ...editForm, isDotacja: e.target.checked })}
                  className="mt-0.5 h-6 w-6 text-purple-600 focus:ring-purple-500 border-gray-400 rounded cursor-pointer"
                  style={{ minWidth: '24px', minHeight: '24px', flexShrink: 0 }}
                />
                <label htmlFor="isDotacja" className="ml-4 text-base font-bold text-gray-900 cursor-pointer select-none">
                  DOTACJA (dotyczy księgowości)
                </label>
              </div>
              {editForm.isDotacja && (
                <div className="bg-purple-50/50 border-2 border-purple-300 rounded-lg p-4 shadow-md space-y-3">
                  <div className="text-sm font-bold text-gray-900 underline">Źródła finansowania zadania:</div>
                  <div className="space-y-2 pl-4">
                    <div className="flex items-center justify-between gap-4">
                      <label className="text-sm text-gray-700 flex-shrink-0">- środki z Powiatu Białostockiego</label>
                      <input
                        type="text"
                        value={editForm.srodkiPowiat || ''}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9,.]/g, '').replace(/(\..*)\./g, '$1');
                          setEditForm({ ...editForm, srodkiPowiat: v });
                        }}
                        className="w-32 px-2 py-1.5 border border-gray-300 rounded text-sm text-right"
                        placeholder="0,00"
                      />
                      <span className="text-sm text-gray-600 w-12">zł</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <label className="text-sm text-gray-700 flex-shrink-0">- środki SP ZOZ w Łapach</label>
                      <input
                        type="text"
                        value={editForm.srodkiSpZoz || ''}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9,.]/g, '').replace(/(\..*)\./g, '$1');
                          setEditForm({ ...editForm, srodkiSpZoz: v });
                        }}
                        className="w-32 px-2 py-1.5 border border-gray-300 rounded text-sm text-right"
                        placeholder="0,00"
                      />
                      <span className="text-sm text-gray-600 w-12">zł</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4 pt-2 border-t-2 border-gray-300">
                    <span className="text-sm font-bold text-gray-900">Wartość wydatku</span>
                    <span className="text-sm font-bold text-gray-900">
                      {(() => {
                        const a = parseFloat((editForm.srodkiPowiat || '0').replace(',', '.')) || 0;
                        const b = parseFloat((editForm.srodkiSpZoz || '0').replace(',', '.')) || 0;
                        const sum = a + b;
                        return sum.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' zł';
                      })()}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Blok podpisywania – nad historią, pod stanowiskiem */}
              {(() => {
                const userSignatures = invoiceSignatures[selectedInvoice?.id]?.filter(
                  sig => sig.user && sig.user.id === user?.id
                ) || [];
                const hasUserSigned = userSignatures.length > 0;
                const currentInvoice = invoices.find(inv => inv.id === selectedInvoice?.id);
                const invoiceStatus = currentInvoice?.status || selectedInvoice?.status;
                const invoiceAssignedUserId = currentInvoice?.assignedUserId || selectedInvoice?.assignedUserId;
                const canSign = invoiceAssignedUserId === user?.id && invoiceStatus === 'IN_PROGRESS';
                const shouldShowSignButton = canSign || 
                  (selectedInvoice?.assignedUserId === user?.id && 
                   (selectedInvoice?.status === 'IN_PROGRESS' || invoiceStatus === 'IN_PROGRESS'));
                if (shouldShowSignButton) {
                  return (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          {hasUserSigned ? (
                            <div className="mb-2">
                              <div className="flex items-center text-sm text-green-800 mb-1">
                                <CheckCircle className="h-4 w-4 mr-2" />
                                <span>Masz już podpis na tej fakturze</span>
                              </div>
                              <span className="text-sm text-blue-800">Możesz dodać nowy opis i podpis po zwrocie faktury</span>
                            </div>
                          ) : (
                            <span className="text-sm text-blue-800">Dodaj swój podpis</span>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            if (user?.canCheckMerit && !editForm.invoicePaymentType) {
                              toast.error('Oznacz typ płatności faktury (gotówkowa lub przelew) przed podpisaniem dokumentu.');
                              return;
                            }
                            setSigningInvoiceId(selectedInvoice.id);
                            setShowSignModal(true);
                          }}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors ml-4"
                        >
                          {hasUserSigned ? 'Podpisz ponownie' : 'Podpisz dokument'}
                        </button>
                      </div>
                    </div>
                  );
                } else if (!canSign) {
                  return (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <X className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600">
                          {invoiceStatus !== 'IN_PROGRESS' 
                            ? `Faktura nie jest w realizacji (status: ${invoiceStatus || 'brak'})` 
                            : `Faktura nie jest przypisana do Ciebie (przypisana do: ${invoiceAssignedUserId}, Ty: ${user?.id})`}
                        </span>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
              <button
                onClick={() => {
                  setShowDescriptionModal(false);
                  setSelectedInvoice(null);
                  setEditForm({
                    description: '',
                    signedByPosition: '',
                    metricalChecked: false,
                    preliminaryCheckDone: false,
                    formalnoRachunkowoChecked: false,
                    zatwierdzonoDo: '',
                    kwota: '',
                    costCenter: '',
                    invoicePaymentType: 'TRANSFER',
                    isDotacja: false,
                    srodkiPowiat: '',
                    srodkiSpZoz: ''
                  });
                }}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Anuluj
              </button>
              <button
                onClick={() => handleSaveDescription(selectedInvoice.id)}
                disabled={saving}
                className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Zapisywanie...' : 'Zapisz opis'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal podpisywania dokumentu */}
      {showSignModal && signingInvoiceId && (
        <DocumentSignModal
          key={signingInvoiceId}
          isOpen={showSignModal}
          onClose={() => {
            setShowSignModal(false);
            setSigningInvoiceId(null);
            setBatchSignQueue([]);
            setSelectedInvoiceIds([]);
          }}
          invoiceId={signingInvoiceId}
          onSuccess={async (signatureData) => {
            try {
              const response = await api.get(`/documents/invoices/${signingInvoiceId}/signatures`);
              setInvoiceSignatures(prev => ({
                ...prev,
                [signingInvoiceId]: response.data || []
              }));
              
              if (signatureData && signatureData.signatureId) {
                try {
                  const requestData = {
                    description: editForm.description || '',
                    signedByPosition: editForm.signedByPosition || user?.position || '',
                    documentSignatureId: signatureData.signatureId.toString()
                  };
                  
                  if (user?.canCheckMerit) {
                    requestData.metricalChecked = editForm.metricalChecked || false;
                    if (editForm.invoicePaymentType) {
                      requestData.invoicePaymentType = editForm.invoicePaymentType;
                    }
                  }
                  
                  if (user?.canDoPreliminaryCheck) {
                    requestData.preliminaryCheckDone = editForm.preliminaryCheckDone || false;
                  }
                  
                  if (user?.canCheckFormalAccounting) {
                    requestData.formalnoRachunkowoChecked = editForm.formalnoRachunkowoChecked || false;
                    if (editForm.zatwierdzonoDo) {
                      requestData.zatwierdzonoDo = editForm.zatwierdzonoDo;
                    }
                    if (editForm.kwota) {
                      requestData.kwota = editForm.kwota.replace(',', '.');
                    }
                  }
                  
                  if (user?.canUseCostCenter) {
                    requestData.costCenter = editForm.costCenter ? editForm.costCenter.trim() : null;
                  }
                  
                  await api.patch(`/invoices/${signingInvoiceId}/describe`, requestData);
                  
                  await fetchInvoiceDescriptionsAndSignatures(signingInvoiceId);
                  
                  setShowDescriptionModal(false);
                  setSelectedInvoice(null);
                  if (batchSignQueue.length === 0) {
                    toast.success('Faktura została podpisana i opis zapisany');
                  }
                } catch (error) {
                  console.error('Błąd podczas zapisywania opisu:', error);
                  toast.error('Błąd podczas zapisywania opisu');
                }
              }
              
              await fetchReturns();
              if (expandedInvoices.has(signingInvoiceId)) {
                await fetchInvoiceHistory(signingInvoiceId);
              }

              // Obsługa masowego podpisywania
              setBatchSignQueue(prev => {
                const remaining = prev.filter(id => id !== signingInvoiceId);
                if (remaining.length > 0) {
                  setSigningInvoiceId(remaining[0]);
                  toast.success(`Podpisano. Zostało ${remaining.length} ${remaining.length === 1 ? 'faktura' : remaining.length < 5 ? 'faktury' : 'faktur'}`);
                  return remaining;
                }
                setShowSignModal(false);
                setSigningInvoiceId(null);
                setSelectedInvoiceIds([]);
                toast.success('Wszystkie zaznaczone faktury zostały podpisane');
                return [];
              });
            } catch (error) {
              console.error('Błąd podczas pobierania podpisów:', error);
            }
          }}
        />
      )}
    </div>
  );
};

export default InvoicesReturns;
